/**
 * Bootstrap Module
 *
 * Handles async initialization of the Office Add-in with service discovery.
 * Includes redirect logic to ensure the add-in is running on the correct domain.
 */
import { STORAGE_KEYS, } from './types';
import { getRegionalEnvironment, createCustomFallbackConfig, isServiceUrl } from './regions';
import { getServiceConfiguration, clearServiceConfigCache } from './service-discovery';
/** Client ID for the Office Add-in */
const CLIENT_ID = 'airia-office-add-in';
/** Maximum redirect attempts to prevent loops */
const MAX_REDIRECT_ATTEMPTS = 2;
/** Cached bootstrap result for synchronous access */
let cachedBootstrapResult = null;
/** Flag to track if migration has been attempted this session */
let migrationAttempted = false;
/** Legacy storage keys from the old system */
const LEGACY_STORAGE_KEYS = {
    SELECTED_ENVIRONMENT: 'airia-selected-environment',
    CUSTOM_URL: 'airia-custom-url',
};
/**
 * Mapping from legacy environment names to new region codes
 * These map to the predefined regional environments
 */
const LEGACY_ENV_TO_REGION = {
    production: 'na',
    eu1: 'eu',
    australia: 'au',
    mena: 'mena',
    singapore: 'sg',
};
/**
 * Mapping from legacy environment names to custom frontend URLs
 * These are custom deployments that don't fit the regional model
 */
const LEGACY_ENV_TO_CUSTOM_URL = {
    // Development environments
    dev: 'https://dev.airiadev.ai',
    staging: 'https://staging.airiadev.ai',
    // Custom tenant deployments
    lippo: 'https://airia.lippomalls.com',
    williams: 'https://williams.airia.ai',
    btlaw: 'https://btlaw.airia.ai',
    demo: 'https://demo.airia.ai',
    internal: 'https://internal.airia.ai',
};
/**
 * Migrate legacy storage keys to the new format
 * This ensures existing users don't lose their environment selection
 */
function migrateLegacyStorage() {
    // Only attempt migration once per session
    if (migrationAttempted) {
        return;
    }
    migrationAttempted = true;
    try {
        // Check if we already have new storage keys set
        const existingRegion = localStorage.getItem(STORAGE_KEYS.SELECTED_REGION);
        if (existingRegion) {
            // Already migrated or user has selected via new UI
            return;
        }
        // Check for legacy storage
        const legacyEnvironment = localStorage.getItem(LEGACY_STORAGE_KEYS.SELECTED_ENVIRONMENT);
        const legacyCustomUrl = localStorage.getItem(LEGACY_STORAGE_KEYS.CUSTOM_URL);
        if (!legacyEnvironment) {
            // No legacy data to migrate
            return;
        }
        console.log('[Migration] Found legacy environment selection:', legacyEnvironment);
        // Handle 'custom' environment with custom URL
        if (legacyEnvironment === 'custom' && legacyCustomUrl) {
            // Check if the legacy URL is a service URL (office, api, identity, etc.)
            // These URLs were valid in the old system but the new system expects app URLs
            if (isServiceUrl(legacyCustomUrl)) {
                console.log('[Migration] Legacy custom URL is a service URL, clearing for re-selection:', legacyCustomUrl);
                // Clear legacy storage so user can re-select
                localStorage.removeItem(LEGACY_STORAGE_KEYS.SELECTED_ENVIRONMENT);
                localStorage.removeItem(LEGACY_STORAGE_KEYS.CUSTOM_URL);
                return;
            }
            console.log('[Migration] Migrating custom URL:', legacyCustomUrl);
            localStorage.setItem(STORAGE_KEYS.CUSTOM_APP_URL, legacyCustomUrl);
            localStorage.setItem(STORAGE_KEYS.SELECTED_REGION, 'custom');
            console.log('[Migration] Successfully migrated custom URL');
            return;
        }
        // Check if it maps to a standard region
        const regionCode = LEGACY_ENV_TO_REGION[legacyEnvironment];
        if (regionCode) {
            console.log('[Migration] Migrating to region:', regionCode);
            localStorage.setItem(STORAGE_KEYS.SELECTED_REGION, regionCode);
            console.log('[Migration] Successfully migrated to region:', regionCode);
            return;
        }
        // Check if it maps to a custom deployment URL
        const customUrl = LEGACY_ENV_TO_CUSTOM_URL[legacyEnvironment];
        if (customUrl) {
            console.log('[Migration] Migrating custom deployment to URL:', customUrl);
            localStorage.setItem(STORAGE_KEYS.CUSTOM_APP_URL, customUrl);
            localStorage.setItem(STORAGE_KEYS.SELECTED_REGION, 'custom');
            console.log('[Migration] Successfully migrated custom deployment');
            return;
        }
        // Unknown legacy environment - log warning but don't clear
        // User will need to re-select their environment
        console.warn('[Migration] Unknown legacy environment:', legacyEnvironment, '- user will need to re-select');
    }
    catch (error) {
        console.error('[Migration] Error during migration:', error);
    }
}
/**
 * Check if we're running in local development
 */
export function isLocalDevelopment() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
}
/**
 * Get the selected region from storage
 */
export function getSelectedRegion() {
    try {
        return localStorage.getItem(STORAGE_KEYS.SELECTED_REGION);
    }
    catch {
        return null;
    }
}
/**
 * Set the selected region in storage
 */
export function setSelectedRegion(region) {
    try {
        localStorage.setItem(STORAGE_KEYS.SELECTED_REGION, region);
        localStorage.removeItem(STORAGE_KEYS.CUSTOM_APP_URL);
        clearServiceConfigCache();
    }
    catch (error) {
        console.warn('[Bootstrap] Error saving selected region:', error);
    }
}
/**
 * Get the custom app URL from storage
 */
export function getCustomAppUrl() {
    try {
        return localStorage.getItem(STORAGE_KEYS.CUSTOM_APP_URL);
    }
    catch {
        return null;
    }
}
/**
 * Set a custom app URL in storage
 */
export function setCustomAppUrl(appUrl) {
    try {
        localStorage.setItem(STORAGE_KEYS.CUSTOM_APP_URL, appUrl);
        localStorage.setItem(STORAGE_KEYS.SELECTED_REGION, 'custom');
        clearServiceConfigCache();
    }
    catch (error) {
        console.warn('[Bootstrap] Error saving custom app URL:', error);
    }
}
/**
 * Clear environment selection
 */
export function clearEnvironmentSelection() {
    try {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_REGION);
        localStorage.removeItem(STORAGE_KEYS.CUSTOM_APP_URL);
        clearServiceConfigCache();
    }
    catch (error) {
        console.warn('[Bootstrap] Error clearing environment selection:', error);
    }
}
/**
 * Check if an environment is selected
 * Also triggers legacy storage migration if needed
 */
export function hasEnvironmentSelection() {
    // Attempt to migrate legacy storage before checking
    migrateLegacyStorage();
    const region = getSelectedRegion();
    if (!region)
        return false;
    if (region === 'custom') {
        const customUrl = getCustomAppUrl();
        if (!customUrl)
            return false;
        // Check if the stored custom URL is a service URL (office, api, identity, etc.)
        // This can happen if a user previously entered an office URL directly
        // The new system expects app URLs, so clear and let user re-select
        if (isServiceUrl(customUrl)) {
            console.log('[Bootstrap] Stored custom URL is a service URL, clearing for re-selection:', customUrl);
            clearEnvironmentSelection();
            return false;
        }
        return true;
    }
    return !!getRegionalEnvironment(region);
}
/**
 * Get the cached bootstrap result (synchronous)
 * Returns null if bootstrap hasn't completed yet
 */
export function getCachedBootstrapResult() {
    return cachedBootstrapResult;
}
/**
 * Get the cached environment config (synchronous)
 * Returns null if bootstrap hasn't completed yet
 */
export function getCachedConfig() {
    return cachedBootstrapResult?.config ?? null;
}
/**
 * Get the cached environment config, throwing if not available.
 * Use this in components that require config to function.
 *
 * IMPORTANT: This function should only be called from components that are
 * rendered AFTER bootstrap completes. If you're seeing this error:
 * 1. Ensure your component is a child of an authenticated view
 * 2. Ensure the entry point gates rendering on bootstrapResult !== null
 * 3. Do not call this in module-level code or early useEffect hooks
 *
 * @throws Error if bootstrap hasn't completed - indicates a programming error
 */
export function getRequiredConfig() {
    const config = getCachedConfig();
    if (!config) {
        throw new Error('[Bootstrap] Configuration not available. ' +
            'This indicates a component is being rendered before bootstrap completes. ' +
            'Ensure this component is only mounted after bootstrapConfig() resolves. ' +
            'See getRequiredConfig() JSDoc for guidance.');
    }
    return config;
}
/**
 * Get redirect attempt count from session storage
 */
function getRedirectAttempts() {
    try {
        const attempts = sessionStorage.getItem(STORAGE_KEYS.REDIRECT_ATTEMPTED);
        return attempts ? parseInt(attempts, 10) : 0;
    }
    catch {
        return 0;
    }
}
/**
 * Increment redirect attempt count
 */
function incrementRedirectAttempts() {
    try {
        const attempts = getRedirectAttempts() + 1;
        sessionStorage.setItem(STORAGE_KEYS.REDIRECT_ATTEMPTED, attempts.toString());
    }
    catch {
        // Ignore errors
    }
}
/**
 * Clear redirect attempt count
 */
function clearRedirectAttempts() {
    try {
        sessionStorage.removeItem(STORAGE_KEYS.REDIRECT_ATTEMPTED);
    }
    catch {
        // Ignore errors
    }
}
/**
 * Build environment config from resolved service config
 */
function buildEnvironmentConfig(resolvedConfig, officeOrigin) {
    // Build Keycloak authority URL
    const authority = `https://${resolvedConfig.authHost}/auth/realms/airia`;
    // Determine base URL for redirect URIs
    // Use the discovered office URL or current origin
    const baseUrl = officeOrigin.replace(/\/$/, '');
    return {
        apiUrl: resolvedConfig.apiUrl,
        hubUrl: resolvedConfig.hubUrl,
        authority,
        clientId: CLIENT_ID,
        popupPostLogoutRedirectUri: `${baseUrl}/authpopup.html?close=true`,
        popupRedirectUri: `${baseUrl}/authpopup.html`,
        redirectUri: `${baseUrl}/taskpane.html`,
    };
}
/**
 * Build fallback config when service discovery fails
 */
function buildFallbackConfig(fallbackApiHost, fallbackAuthHost, fallbackOfficeUrl) {
    const baseUrl = fallbackOfficeUrl.replace(/\/$/, '');
    return {
        apiUrl: `https://${fallbackApiHost}`,
        hubUrl: `https://${fallbackApiHost}/browseragenthub`,
        authority: `https://${fallbackAuthHost}/auth/realms/airia`,
        clientId: CLIENT_ID,
        popupPostLogoutRedirectUri: `${baseUrl}/authpopup.html?close=true`,
        popupRedirectUri: `${baseUrl}/authpopup.html`,
        redirectUri: `${baseUrl}/taskpane.html`,
    };
}
/**
 * Get local development configuration
 */
function getLocalDevConfig() {
    const localOrigin = 'https://localhost:3000';
    const config = {
        // Route API calls through webpack dev server proxy to avoid CORS
        apiUrl: `${localOrigin}/api`,
        hubUrl: `${localOrigin}/browseragenthub`,
        authority: 'https://dev.identity.airiadev.ai/auth/realms/airia',
        clientId: CLIENT_ID,
        popupPostLogoutRedirectUri: `${localOrigin}/authpopup.html?close=true`,
        popupRedirectUri: `${localOrigin}/authpopup.html`,
        redirectUri: `${localOrigin}/taskpane.html`,
    };
    const resolvedConfig = {
        appUrl: 'https://dev.airiadev.ai',
        apiUrl: `${localOrigin}/api`,
        hubUrl: `${localOrigin}/browseragenthub`,
        authHost: 'dev.identity.airiadev.ai',
        officeUrl: localOrigin,
    };
    return { config, resolvedConfig, source: 'local' };
}
/**
 * Resolve discovery URL and fallback config from environment selection
 * Returns null if no valid environment is selected
 */
function resolveEnvironmentConfig() {
    const selectedRegion = getSelectedRegion();
    const customAppUrl = getCustomAppUrl();
    if (!selectedRegion && !customAppUrl) {
        console.log('[Bootstrap] No environment selected');
        return null;
    }
    if (selectedRegion === 'custom' && customAppUrl) {
        console.log('[Bootstrap] Using custom app URL:', customAppUrl);
        return {
            discoveryUrl: customAppUrl,
            fallback: createCustomFallbackConfig(customAppUrl),
        };
    }
    if (selectedRegion) {
        const regionalEnv = getRegionalEnvironment(selectedRegion);
        if (!regionalEnv) {
            console.warn('[Bootstrap] Unknown region:', selectedRegion);
            clearEnvironmentSelection();
            return null;
        }
        console.log('[Bootstrap] Using regional environment:', regionalEnv.name);
        return {
            discoveryUrl: regionalEnv.appUrl,
            fallback: {
                fallbackApiHost: regionalEnv.fallbackApiHost,
                fallbackAuthHost: regionalEnv.fallbackAuthHost,
                fallbackOfficeUrl: regionalEnv.fallbackOfficeUrl,
            },
        };
    }
    console.warn('[Bootstrap] Invalid environment state');
    return null;
}
/**
 * Perform service discovery and build config
 * Shared logic between bootstrapConfig and bootstrapPopupConfig
 *
 * @param discoveryUrl - URL to fetch service configuration from
 * @param fallback - Fallback configuration if discovery fails
 * @param officeOriginOverride - Override for office origin (used by popups)
 * @returns Bootstrap result with resolved config
 */
async function performDiscovery(discoveryUrl, fallback, officeOriginOverride) {
    console.log('[Bootstrap] Attempting service discovery from:', discoveryUrl);
    const resolvedConfig = await getServiceConfiguration(discoveryUrl, fallback.fallbackOfficeUrl);
    // Build fallback result in case discovery fails or we need it
    const fallbackResolvedConfig = {
        appUrl: discoveryUrl,
        apiUrl: `https://${fallback.fallbackApiHost}`,
        hubUrl: `https://${fallback.fallbackApiHost}/browseragenthub`,
        authHost: fallback.fallbackAuthHost,
        officeUrl: fallback.fallbackOfficeUrl,
    };
    const fallbackEnvConfig = buildFallbackConfig(fallback.fallbackApiHost, fallback.fallbackAuthHost, officeOriginOverride || fallback.fallbackOfficeUrl);
    const fallbackResult = {
        config: fallbackEnvConfig,
        resolvedConfig: fallbackResolvedConfig,
        source: 'fallback',
    };
    return { resolvedConfig, fallbackResult };
}
/** Flag to track if URL params have been restored this session */
let urlParamsRestored = false;
/**
 * Restore environment selection from URL parameters
 * This is used after a cross-origin redirect to preserve the user's selection
 * (localStorage is origin-specific, so we pass the selection via URL params)
 *
 * IMPORTANT: Call this BEFORE checking hasEnvironmentSelection() in entry points
 * to ensure cross-origin redirects work correctly.
 */
export function restoreEnvironmentFromUrlParams() {
    // Only restore once per session
    if (urlParamsRestored) {
        return;
    }
    urlParamsRestored = true;
    try {
        const params = new URLSearchParams(window.location.search);
        const region = params.get('_region');
        const customUrl = params.get('_customUrl');
        if (region || customUrl) {
            console.log('[Bootstrap] Found environment params in URL, restoring...');
            if (customUrl) {
                console.log('[Bootstrap] Restoring custom URL from redirect:', customUrl);
                setCustomAppUrl(customUrl);
            }
            else if (region) {
                console.log('[Bootstrap] Restoring region from redirect:', region);
                setSelectedRegion(region);
            }
            // Clean up the URL params (remove _region and _customUrl)
            params.delete('_region');
            params.delete('_customUrl');
            const newSearch = params.toString();
            const newUrl = newSearch
                ? `${window.location.pathname}?${newSearch}`
                : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            console.log('[Bootstrap] Cleaned up URL params');
        }
    }
    catch (error) {
        console.warn('[Bootstrap] Error restoring environment from URL params:', error);
    }
}
/**
 * Bootstrap configuration from service discovery
 *
 * @returns Bootstrap result with config, or null if a redirect is in progress or no env selected
 */
export async function bootstrapConfig() {
    console.log('[Bootstrap] Starting configuration bootstrap...');
    // Restore environment selection from URL params (after cross-origin redirect)
    restoreEnvironmentFromUrlParams();
    // Migrate legacy storage before proceeding
    migrateLegacyStorage();
    // Handle local development
    if (isLocalDevelopment()) {
        console.log('[Bootstrap] Local development detected, using local config');
        clearRedirectAttempts();
        const result = getLocalDevConfig();
        cachedBootstrapResult = result;
        return result;
    }
    // Resolve environment configuration
    const envConfig = resolveEnvironmentConfig();
    if (!envConfig) {
        return null;
    }
    const { discoveryUrl, fallback } = envConfig;
    // Check if we need to redirect BEFORE doing service discovery
    // This avoids CORS issues when fetching service config from a different origin
    const currentOrigin = window.location.origin;
    const expectedOfficeOrigin = new URL(fallback.fallbackOfficeUrl).origin;
    if (currentOrigin !== expectedOfficeOrigin) {
        console.log('[Bootstrap] On wrong origin. Current:', currentOrigin, 'Expected:', expectedOfficeOrigin);
        // Check redirect attempts to prevent infinite loops
        const attempts = getRedirectAttempts();
        if (attempts >= MAX_REDIRECT_ATTEMPTS) {
            console.warn('[Bootstrap] Max redirect attempts reached, proceeding on current origin');
            clearRedirectAttempts();
        }
        else {
            // Redirect to the correct office URL with environment params
            incrementRedirectAttempts();
            const redirectUrl = new URL(window.location.pathname + window.location.search, fallback.fallbackOfficeUrl);
            // Preserve environment selection across redirect via URL params
            const selectedRegion = getSelectedRegion();
            const customAppUrl = getCustomAppUrl();
            if (selectedRegion) {
                redirectUrl.searchParams.set('_region', selectedRegion);
            }
            if (customAppUrl) {
                redirectUrl.searchParams.set('_customUrl', customAppUrl);
            }
            console.log('[Bootstrap] Redirecting to correct origin:', redirectUrl.toString());
            window.location.href = redirectUrl.toString();
            return null; // Signal redirect in progress
        }
    }
    else {
        // We're on the correct origin
        clearRedirectAttempts();
    }
    // Attempt service discovery (now we're on the correct origin, so CORS should work)
    const { resolvedConfig, fallbackResult } = await performDiscovery(discoveryUrl, fallback);
    if (resolvedConfig) {
        console.log('[Bootstrap] Service discovery successful');
        // Build environment config using the current origin (we already redirected if needed)
        const config = buildEnvironmentConfig(resolvedConfig, currentOrigin);
        const result = { config, resolvedConfig, source: 'discovery' };
        cachedBootstrapResult = result;
        return result;
    }
    // Service discovery failed, use fallback
    // We've already redirected to the correct origin if needed, so just use the fallback config
    console.warn('[Bootstrap] Service discovery failed, using fallback configuration');
    cachedBootstrapResult = fallbackResult;
    return fallbackResult;
}
/**
 * Restore environment selection from URL params in popup context
 * The main taskpane passes environment selection to the popup via URL params
 * because Office dialogs may not share localStorage with the taskpane
 */
function restoreEnvironmentFromPopupParams() {
    try {
        const params = new URLSearchParams(window.location.search);
        const region = params.get('_region');
        const customUrl = params.get('_customUrl');
        if (region || customUrl) {
            console.log('[Bootstrap] Found environment params in popup URL');
            if (customUrl) {
                console.log('[Bootstrap] Restoring custom URL from popup params:', customUrl);
                setCustomAppUrl(customUrl);
            }
            else if (region) {
                console.log('[Bootstrap] Restoring region from popup params:', region);
                setSelectedRegion(region);
            }
        }
    }
    catch (error) {
        console.warn('[Bootstrap] Error restoring environment from popup params:', error);
    }
}
/**
 * Bootstrap configuration for popup windows
 * Similar to bootstrapConfig but doesn't perform redirects
 * (popups are already on the correct domain)
 */
export async function bootstrapPopupConfig() {
    console.log('[Bootstrap] Starting popup configuration bootstrap...');
    // Restore environment from URL params passed by the main taskpane
    // This is needed because Office dialogs may not share localStorage
    restoreEnvironmentFromPopupParams();
    // Migrate legacy storage before proceeding
    migrateLegacyStorage();
    // Handle local development
    if (isLocalDevelopment()) {
        console.log('[Bootstrap] Local development detected, using local config');
        const result = getLocalDevConfig();
        cachedBootstrapResult = result;
        return result;
    }
    // Resolve environment configuration
    const envConfig = resolveEnvironmentConfig();
    if (!envConfig) {
        return null;
    }
    const { discoveryUrl, fallback } = envConfig;
    // Attempt service discovery, using current origin for redirect URIs
    const { resolvedConfig, fallbackResult } = await performDiscovery(discoveryUrl, fallback, window.location.origin);
    if (resolvedConfig) {
        // For popups, use the current origin for redirect URIs
        // (the popup is already on the correct domain)
        const config = buildEnvironmentConfig(resolvedConfig, window.location.origin);
        const result = { config, resolvedConfig, source: 'discovery' };
        cachedBootstrapResult = result;
        return result;
    }
    // Use fallback (already has current origin from performDiscovery)
    console.warn('[Bootstrap] Service discovery failed for popup, using fallback');
    cachedBootstrapResult = fallbackResult;
    return fallbackResult;
}
